import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  labelOf,
  FULFILLMENT_TYPE_LABELS,
  PAYMENT_METHOD_LABELS,
  ORDER_STATUS_LABELS,
  CLAIM_TYPE_LABELS,
  CLAIM_REASON_CODE_LABELS,
} from '@/lib/enumLabels';
import { Badge, Button, PageShell, SectionCard, Separator, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableFooter } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import { COUNTRIES } from '@/lib/countries/countryCodes';
import { countryLabel } from '@/lib/countries/nationalityOptions';
import type {
  AdminOrderActionRequest,
  AdminRole,
  OrderDetail,
} from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import { useOrderQuery } from '../hooks/useOrderQuery';
import {
  useOrderActionMutation,
  type OrderActionType,
} from '../hooks/useOrderActionMutation';
import { OrderStatusPill } from '../components/OrderStatusPill';
import { ItemRefundNote, hasItemRefund } from '../components/ItemRefundNote';
import { OrderActionModal } from '../components/OrderActionModal';
import { canPerformAction } from '../lib/orderActionRbac';
import { OrderPickupConfirmModal } from '../components/OrderPickupConfirmModal';
import { useOrderPickupConfirmMutation } from '../hooks/useOrderPickupConfirmMutation';
import { canConfirmOrderPickup } from '../lib/orderPickupRbac';
import { useConfirmPurchaseMutation } from '../hooks/useConfirmPurchaseMutation';
import { canCancelPendingOrder, canConfirmPurchase } from '../lib/confirmPurchaseRbac';
import { useConfirmDepositMutation, useRestorePendingMutation } from '../hooks/useConfirmDepositMutation';
import {
  canConfirmDeposit,
  isBankTransferPendingOrder,
  isRestorableCanceledBankTransferOrder,
} from '../lib/confirmDepositRbac';
import { DepositConfirmModal } from '../components/DepositConfirmModal';
import { canMarkFulfillment, isFulfillmentProgressed } from '../lib/fulfillmentReadyGate';
import { formatOrderItemOption } from '../lib/orderItemOption';
import { migratedInputItemIds, placeOrderItemInputs, readOrderInputRows } from '../lib/orderItemInputs';
import { ORDER_MALL_LABELS } from '../lib/orderMall';
import { OrderFormFieldValuesInline } from '@/features/orderFormFields/components/OrderFormFieldValuesSection';
import { ProductCollectionInputsSection } from '@/features/productCollectionInputs/components/ProductCollectionInputsSection';
import {
  useProductCollectionDisplay,
  type ProductCollectionGroup,
} from '@/features/productCollectionInputs/hooks/useProductCollectionInputs';
import { EventApplicationInputsSection } from '../components/EventApplicationInputsSection';
import { OrderAdminMemoSection } from '../components/OrderAdminMemoSection';
import { OrderAdminMemoBoard } from '../components/OrderAdminMemoBoard';
import { OrderPaymentSyncDialog } from '../components/OrderPaymentSyncDialog';
import { useOrderPaymentRefundFlags } from '../hooks/useOrderPaymentRefundFlags';
import { OrderStatusChangeForm } from '../components/OrderStatusChangeForm';
import { OrderItemCancelPanel } from '../components/OrderItemCancelPanel';
import { OrderLogDialog } from '../components/OrderLogDialog';
import { TrackingButton, type TrackableShipment } from '../components/TrackingButton';
import { OrderInfoEditDialog, type OrderInfoValues } from '../components/OrderInfoEditDialog';
import { OrderPgOrderNo } from '../components/OrderPgOrderNo';
import { OrderManualRefundDialog, OrderPaymentStatusBox, type PaymentStatusTarget } from '../components/OrderPaymentStatusBox';
import { OrderCashReceiptSection } from '../components/OrderCashReceiptSection';
import { labelNameForCode } from '../lib/displayLabelCode';
import { GuestOrderLookupInfo } from '../components/GuestOrderLookupInfo';
import { PrintDocumentMenu } from '../printDocuments/PrintDocumentMenu';
import {
  extractOrderCharge,
  formatChargeAmount,
  formatExchangeRate,
  formatKrwLedger,
} from '../lib/chargeMoney';
import { CnyReference } from '../components/CnyReference';
import { PgRecoverRunner } from '../components/PgRecoverRunner';

const formatMoney = (m: { amount: number; currencyCode: string }): string =>
  `${m.amount.toLocaleString()} ${m.currencyCode}`;

// [pay-currency] 결제금액 행 — KRW 원장은 ₩ 기호 표기, 비-KRW Money 는(방어) 기존 포맷.
const formatPaymentAmount = (m: { amount: number; currencyCode: string }): string =>
  m.currencyCode === 'KRW' ? formatKrwLedger(m.amount) : formatMoney(m);

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * [233] 해외(글로벌) 배송지 — be-order ShippingAddressDto 의 확장 필드. generated 타입
 * (OrderShipmentInfo.shippingAddress)에 반영되기 전까지 read 전용으로 안전 캐스팅한다.
 * 국내 주문은 확장 필드가 미전송(undefined) → 렌더에서 자연 생략된다.
 */
interface GlobalShippingAddress {
  zipCode: string;
  address1: string;
  address2?: string | null;
  receiverName?: string | null;
  receiverMobile?: string | null;
  recipientEnglishName?: string | null;
  recipientPhone?: string | null;
  recipientMobile?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  countryCode?: string | null;
  remarks?: string | null;
}

/** ISO alpha-2 국가코드 → 현재 로케일 표시명. 미등록 코드만 코드 그대로 폴백. */
const resolveCountryName = (
  code: string | null | undefined,
  locale: string | undefined,
): string | null => {
  if (!code) return null;
  const match = COUNTRIES.find((c) => c.code.toUpperCase() === code.toUpperCase());
  return match ? countryLabel(match, locale) : code;
};

/** 도로명 주소(주소1 + 주소2) 한 줄 — 도시·주/도·우편번호는 각각 별도 행으로 노출. */
const formatStreetAddress = (addr: GlobalShippingAddress): string => {
  const parts = [addr.address1, addr.address2].filter((s): s is string => Boolean(s));
  return parts.length > 0 ? parts.join(' ') : '-';
};

const hasOpenCancelRequest = (order: OrderDetail): boolean =>
  order.claimStatus === 'REQUESTED' ||
  order.claimStatus === 'UNDER_REVIEW' ||
  order.orderStatus === 'CANCEL_REQUESTED';

const isDelivery = (order: OrderDetail): boolean => order.fulfillmentType === 'DELIVERY';

/**
 * QA-454 — [주문 정보 수정]은 입금대기·결제완료·상품준비중 주문만(출고 뒤에는 송장·배송지가 이미 넘어감).
 * displayLabelCode 가 오면 그것으로, 없으면 orderStatus 로 본다.
 */
// 픽업 주문은 픽업 전용 라벨 코드 — 상품준비 대기·픽업 준비완료일 때 주문자·배송 메모만(order #69).
const ORDER_INFO_EDITABLE_LABEL_CODES: ReadonlySet<string> = new Set([
  'AWAITING_DEPOSIT',
  'PAID',
  'PREPARING',
  'PICKUP_WAITING_PREP',
  'PICKUP_READY',
]);
const ORDER_INFO_EDITABLE_STATUSES: ReadonlySet<string> = new Set(['PENDING_PAYMENT', 'PAYMENT_CONFIRMED', 'READY_FOR_FULFILLMENT']);
const isOrderInfoEditable = (order: OrderDetail): boolean => {
  const code = (order as { displayLabelCode?: string | null }).displayLabelCode;
  return code ? ORDER_INFO_EDITABLE_LABEL_CODES.has(code) : ORDER_INFO_EDITABLE_STATUSES.has(String(order.orderStatus ?? ''));
};

/** 배송 메모(운송장 인쇄) — BE 응답 키 확정 전이라 deliveryMemo, 없으면 주문 시 입력한 deliveryRequestMessage. */
const deliveryMemoOf = (order: OrderDetail): string => {
  const o = order as { deliveryMemo?: string | null; deliveryRequestMessage?: string | null };
  return (o.deliveryMemo ?? o.deliveryRequestMessage ?? '').trim();
};

const orderInfoValuesOf = (order: OrderDetail): OrderInfoValues => {
  const addr = (order.shipmentInfo?.shippingAddress ?? null) as GlobalShippingAddress | null;
  return {
    ordererName: order.ordererName ?? '',
    ordererEmail: order.ordererEmail ?? '',
    ordererMobile: order.ordererMobile ?? '',
    shipping: isDelivery(order)
      ? {
          receiverName: addr?.receiverName ?? '',
          countryCode: addr?.countryCode ?? 'KR',
          city: addr?.city ?? '',
          stateProvince: addr?.stateProvince ?? '',
          zipCode: addr?.zipCode ?? '',
          address1: addr?.address1 ?? '',
          address2: addr?.address2 ?? '',
          receiverMobile: addr?.receiverMobile ?? addr?.recipientMobile ?? addr?.recipientPhone ?? '',
        }
      : null,
    deliveryMemo: deliveryMemoOf(order),
  };
};
const isPickup = (order: OrderDetail): boolean => order.fulfillmentType === 'PICKUP';

/**
 * 구매확정 가능 여부 — BE 가 권위적으로 판단해 `canConfirmPurchase` / `availableActions`
 * (CONFIRM) 로 신호한다. 둘 중 하나라도 참이면 노출(BE 단일 진실 소스, FE 비파생).
 */
const canShowConfirmPurchase = (order: OrderDetail): boolean =>
  order.canConfirmPurchase === true ||
  (order.availableActions?.includes('CONFIRM') ?? false);

/** QA-432 — PG 상태 동기화 권한(주문 취소 승인과 같은 운영 역할). */
const PAYMENT_SYNC_ROLES: ReadonlyArray<AdminRole> = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'];
/** QA-457 — 주문 상태 변경(order #71 @PreAuthorize 와 같음). 구매확정은 canConfirmPurchase(OPS·SUPER). */
const ORDER_STATUS_CHANGE_ROLES: ReadonlyArray<AdminRole> = ['OPS_ADMIN', 'CS_AGENT', 'FULFILLMENT_AGENT', 'SUPER_ADMIN'];
/**
 * 결제 전이거나 이미 끝난(취소·만료·실패) 주문은 맞출 결제가 없다. 구매확정(COMPLETED)은 적립까지 끝나 PG 환불을 자동으로
 * 맞추지 않는다(클레임으로 처리) — 버튼을 숨긴다. 구매확정은 CONFIRMED 로 오는 응답도 있어 함께 숨긴다.
 */
const PAYMENT_SYNC_HIDDEN_STATUSES: ReadonlySet<string> = new Set(['CREATED', 'PENDING_PAYMENT', 'CANCELED', 'EXPIRED', 'FAILED', 'COMPLETED', 'CONFIRMED']);

/** QA-510 택배사 표시 — 마스터 이름(shipmentInfo.courierName, 예 'CJ대한통운'), 없으면 코드(이관 korex·D30 등). */
const courierDisplayName = (info: { courierCode?: string | null } | null | undefined): string | null =>
  (info as { courierName?: string | null } | null | undefined)?.courierName ?? info?.courierCode ?? null;

export const OrderDetailPage = () => {
  const { t, i18n } = useTranslation('orders');
  const locale = i18n.language;
  const { orderId } = useParams<{ orderId: string }>();
  const query = useOrderQuery(orderId);
  const { currentAdmin } = useAuthContext();
  const roles = useMemo<ReadonlyArray<AdminRole>>(
    () => currentAdmin?.roles ?? [],
    [currentAdmin],
  );

  const actionMutation = useOrderActionMutation();
  const pickupConfirmMutation = useOrderPickupConfirmMutation();
  const confirmPurchaseMutation = useConfirmPurchaseMutation();
  const confirmDepositMutation = useConfirmDepositMutation();
  const restorePendingMutation = useRestorePendingMutation();
  const [activeAction, setActiveAction] = useState<OrderActionType | null>(null);
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [confirmPurchaseOpen, setConfirmPurchaseOpen] = useState(false);
  const [confirmDepositOpen, setConfirmDepositOpen] = useState(false);
  const [restorePendingOpen, setRestorePendingOpen] = useState(false);
  const [paymentSyncOpen, setPaymentSyncOpen] = useState(false);
  const [manualRefundOpen, setManualRefundOpen] = useState(false);
  const [infoEditOpen, setInfoEditOpen] = useState(false);
  // QA-302 — 상품 추가입력을 주문상품 표 품목 행에 함께(원문보기 결과도 공유).
  const collection = useProductCollectionDisplay(orderId);
  // QA-552 주문 로그 창.
  const [logOpen, setLogOpen] = useState(false);
  const canPaymentSync = PAYMENT_SYNC_ROLES.some((r) => roles.includes(r));
  const paymentRefund = useOrderPaymentRefundFlags(orderId, canPaymentSync);
  const [pgRecoverRunning, setPgRecoverRunning] = useState(false);

  if (query.isPending) {
    return (
      <p className="py-8 text-center text-sm text-[color:var(--content-muted)]">
        {t('orders.detail.loading')}
      </p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <p
        className="py-8 text-center text-sm"
        role="alert"
        style={{ color: 'var(--status-danger-fg)' }}
      >
        {t('orders.detail.error')}
      </p>
    );
  }

  const order = query.data;
  // 주문상품 표에 붙은 상품코드 — 상품 추가입력 섹션에서는 빼서 두 번 보이지 않게 한다.
  // 이관 주문 품목(order-service 값이 기준)의 상품코드도 뺀다 — catalog 쪽 이관 당시 값이 따로 보이지 않게.
  const migratedItemIds = migratedInputItemIds(order.items, readOrderInputRows(order));
  const inlineCollectionCodes = new Set(
    [
      ...collection.groups
        .map((g) => g.productCode)
        .filter((code) => code && order.items.some((i) => (i as unknown as { productCode?: string | null }).productCode === code)),
      ...order.items
        .filter((i) => migratedItemIds.has(i.orderItemId))
        .map((i) => (i as unknown as { productCode?: string | null }).productCode),
    ].filter((c): c is string => !!c),
  );

  // [pay-currency] 해외몰 외화 청구 주문 — detail 의 charge(ChargeMoney)+exchangeRate(T1 동결).
  // KRW 주문은 키 생략(NON_NULL) → null = 기존 렌더 그대로(추가 행 없음).
  const foreignCharge = extractOrderCharge(order);

  // 국가몰(country-mall) — be-order-mall `mall` 필드(generated 타입 반영 전까지 안전 캐스팅).
  // 회계 국가별 확인용. 미전송(구주문)/미지원 코드는 labelOf 가 '-' 폴백(코드 원문 미노출).
  const orderMall = (order as { mall?: string | null }).mall ?? null;

  // [guest-policy] 비회원 주문 식별 — be-guest-policy 가 order 상세에 `customerType`(userId==null→GUEST)
  // 을 추가한다. generated 타입(types.gen.ts) 반영 전까지 안전 캐스팅으로 읽는다. 비번 노출 정책은
  // 폐기됐고, CS 는 주문자명(이미 노출) + 주문번호로 식별 → GUEST 배지로 비회원 주문임만 표시한다
  // (회원/미전송 구주문은 배지 미표기 → 기존 렌더 그대로).
  const isGuestOrder =
    (order as { customerType?: 'MEMBER' | 'GUEST' }).customerType === 'GUEST';

  const handleSubmit = (body: AdminOrderActionRequest) => {
    if (!activeAction || !orderId) return;
    actionMutation.mutate(
      { orderId, action: activeAction, body },
      {
        onSuccess: () => setActiveAction(null),
      },
    );
  };

  const actionErrorMessage = actionMutation.error
    ? friendlyMessage(actionMutation.error, t('orders.actionModal.unknownError'))
    : null;

  /**
   * 액션 노출 플래그 — 이커머스 어드민 패턴. 기존 `disabled={!RBAC || !상태조건}`(항상 표시,
   * 비활성)을 "권한 보유 + 상태 충족일 때만 렌더"로 전환한다. 권한·상태 게이트 자체는
   * 무수정(회귀0); 노출 표현만 disabled→조건부 렌더로 바뀐다. 권한이 있어도 상태 미충족이면
   * 숨김, 권한 자체가 없으면 당연히 숨김 → 현 단계에 적용 가능한 액션만 깔끔히 보인다.
   */
  // order #110 — 결제 후 취소는 CANCEL 클레임으로 접수된다. 승인 전(신청·검토) 취소 클레임이 있으면 주문의 [취소 승인·반려]는
  //   BE 가 409 로 막으니 숨기고, 클레임 상세로 보낸다. 클레임과 연결 안 된 예전 취소 요청만 여기서 처리한다.
  const pendingCancelClaim =
    (order.claims ?? []).find((c) => c.claimType === 'CANCEL' && (c.claimStatus === 'REQUESTED' || c.claimStatus === 'UNDER_REVIEW')) ??
    null;
  const showCancelApprove =
    canPerformAction('cancel-approve', roles) && hasOpenCancelRequest(order) && !pendingCancelClaim;
  const showCancelReject =
    canPerformAction('cancel-reject', roles) && hasOpenCancelRequest(order) && !pendingCancelClaim;
  const showFulfillmentReady =
    canPerformAction('fulfillment-ready', roles) &&
    isDelivery(order) &&
    canMarkFulfillment(order);
  // QA-625 — [이행 실패]는 숨긴다. 처리상태를 결제실패(FAILED)로 바꿔 주문이 결제실패로 잘못 보였고, 배송실패 상태도 쓰지 않는다.
  const showFulfillmentFail = false;
  // QA-371 — 배송중(FULFILLING) DELIVERY 주문을 관리자가 배송완료로. BE 도 FULFILLING 에서만 받는다(그 외 409).
  // 택배사 연동은 없고, 수동 처리가 없으면 배송중 후 N일(정책, 기본 30일) 새벽 배치가 자동 배송완료한다.
  const showFulfillmentComplete =
    canPerformAction('fulfillment-complete', roles) &&
    isDelivery(order) &&
    (order as { fulfillmentStatus?: string | null }).fulfillmentStatus === 'FULFILLING';
  const showPickupConfirm =
    canConfirmOrderPickup(roles) && isPickup(order) && Boolean(order.pickupInfo?.pickupId);
  const showConfirmDeposit =
    canConfirmDeposit(roles) && isBankTransferPendingOrder(order);
  // QA-472 — 결제된 적 없는 무통장 취소 주문(이관 때 입금대기 → 취소로 들어온 주문 등)을 입금대기로 되돌린다(order #77).
  const showRestorePending =
    canConfirmDeposit(roles) && isRestorableCanceledBankTransferOrder(order);
  const showConfirmPurchase =
    canConfirmPurchase(roles) && canShowConfirmPurchase(order);
  // QA-432 — [PG 상태 동기화]. 토스 상점관리자에서 직접 취소·환불하면 주문이 결제완료로 남아, 결제가 끝난(카드 등 PG) 주문에서
  // 토스 기준으로 다시 맞춘다. 결제 전·이미 취소/만료된 주문은 맞출 것이 없다.
  // QA-432 재요청 — [결제상태 변경] 박스(드롭다운 + 버튼). 고를 수 있는 상태만 목록에 넣고 기존 처리에 잇는다.
  //   결제완료: 무통장 입금대기 → 입금확인
  //   수기 취소: 토스 결제키가 없는 결제(고도몰 이관·수기주문·자체 무통장 — manualRefundOnly)만 → 수기 환불 기록
  //   토스 취소: 토스 결제(결제키 있음) → 토스에서 취소한 결제를 다시 조회해 반영(PG 상태 동기화). 이관·무통장은 토스 결제가 없어 뺀다(BE 회신).
  //   토스 계좌이체·가상계좌는 토스 취소만(BE→BO 2026-10-21) — 수기 기록이 필요하면 결제 상세 [환불 상태 변경(토스 수기 환불)].
  const paymentSyncEligible = canPaymentSync && !PAYMENT_SYNC_HIDDEN_STATUSES.has(String(order.orderStatus ?? ''));
  const refundPayment = paymentRefund.payment;
  const refundFlags = paymentRefund.flags;
  const canManualRefund =
    paymentSyncEligible && Boolean(refundPayment) && refundFlags?.pgCancelled !== true && refundFlags?.manualOnly === true;
  const canPgCancelSync = paymentSyncEligible && refundFlags?.manualOnly !== true;
  // QA-599 — [PG 확인 후 결제완료]. 표시 상태 '고객결제중단'(PAYMENT_STOPPED)이고 무통장 직접입금이 아닐 때(order #126).
  const showPgRecover =
    canPaymentSync &&
    (order as { displayLabelCode?: string | null }).displayLabelCode === 'PAYMENT_STOPPED' &&
    order.paymentMethod !== 'BANK_TRANSFER';
  const paymentStatusTargets: PaymentStatusTarget[] = [
    // QA-487 — 관리자취소된 무통장 주문도 이 칸에서 입금대기로(취소 복원) 되돌린다(하단 버튼만으론 못 찾았다).
    ...(showRestorePending ? (['RESTORE'] as const) : []),
    ...(showConfirmDeposit ? (['PAID'] as const) : []),
    ...(showPgRecover ? (['PG_RECOVER'] as const) : []),
    ...(canManualRefund ? (['REFUNDED'] as const) : []),
    ...(canPgCancelSync ? (['CANCELED'] as const) : []),
  ];
  const onPaymentStatusSelect = (target: PaymentStatusTarget) => {
    if (target === 'RESTORE') setRestorePendingOpen(true);
    else if (target === 'PAID') setConfirmDepositOpen(true);
    else if (target === 'PG_RECOVER') {
      if (!window.confirm('토스에서 결제 완료를 확인하고 결제완료로 변경합니다')) return;
      setPgRecoverRunning(true);
    }
    else if (target === 'REFUNDED') setManualRefundOpen(true);
    else setPaymentSyncOpen(true);
  };

  // '배송 처리됨' 안내 — 출고가 이미 진전된 DELIVERY 주문에서, 적용 가능한 출고 액션이
  // 없을 때만 노출(액션 버튼이 보이면 그 자체로 충분한 신호).
  const showFulfillmentProcessedHint =
    isDelivery(order) &&
    isFulfillmentProgressed(order) &&
    !showFulfillmentReady &&
    !showFulfillmentFail &&
    !showFulfillmentComplete;

  // 진행/클레임 그룹에 노출할 액션이 하나라도 있는지 — 그룹 구분선(Separator) 렌더 판단.
  const hasProgressAction =
    showConfirmDeposit ||
    showRestorePending ||
    showFulfillmentReady ||
    showFulfillmentFail ||
    showFulfillmentComplete ||
    showPickupConfirm ||
    showConfirmPurchase;
  const hasClaimAction = showCancelApprove || showCancelReject || pendingCancelClaim != null;
  // 운영 액션이 하나라도 있는지 — 완료/취소 주문 등 0개일 때 '추가 작업 없음' 힌트로 대체
  // (출력물 메뉴는 항상 유지 → footer 가 어색한 빈 바가 되지 않도록).
  const hasAnyOperationAction = hasProgressAction || hasClaimAction;

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('orders.detail.title', { orderNo: order.orderNo })}
          <OrderStatusPill
            displayStatus={order.displayStatus}
            displayLabelCode={
              (order as { displayLabelCode?: string | null }).displayLabelCode
            }
            orderId={order.orderId}
          />
        </span>
      }
      breadcrumb={[
        { label: t('orders.list.title'), href: '/orders' },
        { label: order.orderNo },
      ]}
    >

      {/* pb 확보 — 하단 sticky 액션 바가 마지막 섹션(클레임 등)을 가리지 않도록
          콘텐츠 영역 끝에 여백을 둔다(바 높이 + 여유). */}
      {/* QA-370 — 요약 칸 320→380px: 주문번호(ORD-…-XXXXX)가 한 줄에 들어가는 폭. */}
      <div className="grid gap-6 pb-20 lg:grid-cols-[380px_minmax(0,1fr)]">
        <SectionCard className="space-y-4 self-start lg:sticky lg:top-4">
          <InfoBlock label={t('orders.detail.orderNo')} value={order.orderNo} />
          {/* QA-573 토스 상점관리자 조회용 PG 주문번호(= 주문 UUID). */}
          <OrderPgOrderNo orderId={order.orderId} flags={refundFlags} />
          <InfoBlock
            label={t('orders.detail.orderedAt')}
            value={formatDateTime(order.orderedAt)}
          />
          <InfoBlock
            label={t('orders.detail.paidAt')}
            value={formatDateTime(order.paidAt)}
          />
          {/* 국가몰 — 어떤 국가몰에서 결제됐는지(회계 국가별). 값이 있을 때만 행 노출
              (구주문/미전송은 자연 생략, 미지원 코드는 labelOf 가 '-' 폴백). */}
          {orderMall ? (
            <InfoBlock
              label={t('orders.detail.mall')}
              value={labelOf(ORDER_MALL_LABELS, orderMall)}
            />
          ) : null}
          <InfoBlock
            label={t('orders.detail.fulfillmentType')}
            value={labelOf(FULFILLMENT_TYPE_LABELS, order.fulfillmentType)}
          />
          <InfoBlock
            label={t('orders.detail.paymentMethod')}
            value={labelOf(PAYMENT_METHOD_LABELS, order.paymentMethod)}
          />
          {/* [pay-currency] 결제 금액 = KRW 원장(₩ 고정 표기, 태깅 계약 SSoT). 외화 청구 주문은
              실청구액(charge) 행 + T1 동결 환율을 별도 노출해 원장/청구를 혼동 없이 구분한다. */}
          <InfoBlock
            label={t('orders.detail.paymentAmount')}
            value={formatPaymentAmount(order.paymentAmount)}
          />
          {foreignCharge ? (
            <div data-testid="order-charge-amount">
              <InfoBlock
                label={t('orders.detail.chargeAmount', '청구 금액')}
                value={
                  <>
                    {formatChargeAmount(foreignCharge.charge.amount, foreignCharge.charge.currencyCode)}
                    <CnyReference
                      amount={foreignCharge.charge.amount}
                      currencyCode={foreignCharge.charge.currencyCode}
                      mall={orderMall}
                    />
                  </>
                }
              />
              {foreignCharge.exchangeRate != null ? (
                <p
                  className="-mt-1 mb-2 text-xs"
                  style={{ color: 'var(--content-muted)' }}
                  data-testid="order-charge-rate"
                >
                  {t('orders.detail.chargeRateHint', {
                    rate: formatExchangeRate(
                      foreignCharge.exchangeRate,
                      foreignCharge.charge.currencyCode,
                    ),
                    defaultValue: '적용 환율(주문 시점 동결) {{rate}}',
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="border-t pt-3" style={{ borderColor: 'var(--surface-1)' }}>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[color:var(--content-strong)]">
              {t('orders.detail.orderer')}
              {/* [guest-policy] 비회원 주문 식별 배지 — GUEST 일 때만 노출(회원/구주문은 미표기).
                  비번 노출은 폐기, CS 는 주문자명+주문번호로 식별하므로 비회원 여부만 시각 표시. */}
              {isGuestOrder ? (
                <Badge variant="info" size="sm" data-testid="order-guest-badge">
                  {t('orders.detail.guestBadge', '비회원')}
                </Badge>
              ) : null}
            </h3>
            {/* 비회원 주문조회 정보 — CS 가 "주문자명 + 주문번호" 조회 방법을 바로 안내하도록. 값이 없으면 숨김. */}
            {isGuestOrder && order.ordererName && order.orderNo ? (
              <GuestOrderLookupInfo ordererName={order.ordererName} orderNo={order.orderNo} />
            ) : null}
            <InfoBlock
              label={t('orders.detail.ordererName')}
              value={order.ordererName ?? '-'}
            />
            <InfoBlock
              label={t('orders.detail.ordererEmail')}
              value={order.ordererEmail ?? '-'}
            />
            <InfoBlock
              label={t('orders.detail.ordererMobile')}
              value={order.ordererMobile ?? '-'}
            />
          </div>

          {/* QA-576 — 주문서 추가정보(중국몰 신분증 번호·위챗 등)를 주문자 아래에(고도몰 주문 상세 "추가 정보" 위치). */}
          <OrderFormFieldValuesInline orderId={order.orderId} />

          {order.pickupInfo ? (
            <div
              className="border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
              data-sentry-mask
            >
              <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
                {t('orders.detail.pickup')}
              </h3>
              <InfoBlock
                label={t('orders.detail.pickupStore')}
                value={order.pickupInfo.storeName}
              />
              <InfoBlock
                label={t('orders.detail.pickupSlot')}
                value={`${order.pickupInfo.slotDate} ${order.pickupInfo.slotTimeRange}`}
              />
              <InfoBlock
                label={t('orders.detail.pickupDeadline')}
                value={formatDateTime(order.pickupInfo.deadlineAt)}
              />
              {order.pickupInfo.pickupCode ? (
                <InfoBlock
                  label={t('orders.detail.pickupCode')}
                  value={order.pickupInfo.pickupCode}
                />
              ) : null}
            </div>
          ) : null}

          {order.shipmentInfo ? (
            <div className="border-t pt-3" style={{ borderColor: 'var(--surface-1)' }}>
              <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
                {t('orders.detail.shipment')}
              </h3>
              {/* [233/QA-233] 배송지 — be-order ShippingAddressDto 의 확장 필드는 generated 타입 반영
                  전까지 안전 캐스팅으로 읽는다. 국적/몰 분기 없이 값이 있는 필드만 각 행으로 노출한다
                  (값 없는 필드는 자연 생략). 국가는 ISO 코드를 로케일 표시명으로 변환(KR → "대한민국"). */}
              {(() => {
                const addr = order.shipmentInfo!.shippingAddress as GlobalShippingAddress | null;
                if (!addr) {
                  return (
                    <InfoBlock label={t('orders.detail.shippingAddress')} value="-" />
                  );
                }
                const countryName = resolveCountryName(addr.countryCode, locale);
                // 전화번호 — BE(OrderResponseAssembler) 기준: 해외몰은 recipientPhone=유선·recipientMobile=휴대폰 두 줄.
                //   국문몰은 recipientMobile 이 없고 대표 연락처 하나가 recipientPhone·receiverMobile 두 칸에 같은 값으로
                //   온다 → "수령인 연락처" 한 줄만(예전엔 유선전화·휴대폰 두 줄에 같은 번호가 보였다).
                const hasSeparateMobile = Boolean(addr.recipientMobile);
                const contactPhone = addr.recipientPhone ?? addr.receiverMobile;
                // QA-233 요청 화면 순서: 수령인 → 수령인(영문) → 국가 → 도시 → 주 → 주소 → 우편번호 → 유선전화 → 휴대폰 → 비고.
                // 수령인과 영문 수령인은 따로 보인다(예전엔 영문명이 있으면 원래 이름을 덮어 운영자가 실제 수령인을 못 봤다).
                return (
                  <>
                    {addr.receiverName ? (
                      <InfoBlock
                        label={t('orders.detail.recipientName')}
                        value={addr.receiverName}
                      />
                    ) : null}
                    {addr.recipientEnglishName ? (
                      <InfoBlock
                        label={t('orders.detail.recipientEnglishName')}
                        value={addr.recipientEnglishName}
                      />
                    ) : null}
                    {countryName ? (
                      <InfoBlock
                        label={t('orders.detail.recipientCountry')}
                        value={countryName}
                      />
                    ) : null}
                    {addr.city ? (
                      <InfoBlock
                        label={t('orders.detail.recipientCity')}
                        value={addr.city}
                      />
                    ) : null}
                    {addr.stateProvince ? (
                      <InfoBlock
                        label={t('orders.detail.recipientStateProvince')}
                        value={addr.stateProvince}
                      />
                    ) : null}
                    <InfoBlock
                      label={t('orders.detail.shippingAddress')}
                      value={formatStreetAddress(addr)}
                    />
                    {addr.zipCode ? (
                      <InfoBlock
                        label={t('orders.detail.recipientZipCode')}
                        value={addr.zipCode}
                      />
                    ) : null}
                    {hasSeparateMobile ? (
                      <>
                        {addr.recipientPhone ? (
                          <InfoBlock
                            label={t('orders.detail.recipientPhone')}
                            value={addr.recipientPhone}
                          />
                        ) : null}
                        <InfoBlock
                          label={t('orders.detail.recipientMobile')}
                          value={addr.recipientMobile ?? ''}
                        />
                      </>
                    ) : contactPhone ? (
                      <InfoBlock
                        label={t('orders.detail.recipientContact', '수령인 연락처')}
                        value={contactPhone}
                      />
                    ) : null}
                    {addr.remarks ? (
                      <InfoBlock
                        label={t('orders.detail.recipientRemarks')}
                        value={addr.remarks}
                      />
                    ) : null}
                    {deliveryMemoOf(order) ? (
                      <InfoBlock
                        label={t('orders.detail.deliveryMemo', '배송 메모')}
                        value={deliveryMemoOf(order)}
                      />
                    ) : null}
                  </>
                );
              })()}
              <InfoBlock
                label={t('orders.detail.courier')}
                value={courierDisplayName(order.shipmentInfo) ?? '-'}
              />
              <InfoBlock
                label={t('orders.detail.trackingNo')}
                value={
                  // QA-526 — 송장번호 옆 [배송추적](택배사 배송조회 팝업).
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {order.shipmentInfo.trackingNo ?? '-'}
                    <TrackingButton info={order.shipmentInfo as TrackableShipment} />
                  </span>
                }
              />
            </div>
          ) : null}
          {/* QA-454 — [주문 정보 수정]. 입금대기·결제완료·상품준비중에서만 누를 수 있다. */}
          {canPaymentSync ? (
            <div className="border-t pt-3 text-center" style={{ borderColor: 'var(--surface-1)' }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={!isOrderInfoEditable(order)}
                title={
                  isOrderInfoEditable(order)
                    ? undefined
                    : t('orders.infoEdit.disabledHint', '입금대기·결제완료·상품준비중(픽업은 준비 대기·준비완료) 주문만 수정할 수 있습니다.')
                }
                onClick={() => setInfoEditOpen(true)}
                data-action="order-info-edit"
              >
                {t('orders.infoEdit.title', '주문 정보 수정')}
              </Button>
            </div>
          ) : null}
        </SectionCard>

        <div className="space-y-6">
          <SectionCard
            title={
              <span className="flex items-center gap-2">
                {t('orders.detail.items')}
                {/* QA-552 고도몰 [주문 로그 보기] — 이 주문의 상태 변경 이력과 처리자. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[12px]"
                  onClick={() => setLogOpen(true)}
                  data-testid="order-log-button"
                >
                  {t('orders.detail.orderLog', '주문 로그')}
                </Button>
              </span>
            }
          >
            <OrderItemsTable items={order.items} order={order} collectionGroups={collection.groups} collectionRaw={collection.isRaw} />
            {/* QA-520(order #111) 상품별 부분취소 — 결제 완료 후 출고 전, 상품 2개 이상일 때만. */}
            <OrderItemCancelPanel orderId={order.orderId} orderStatus={order.orderStatus} items={order.items} roles={roles} />
          </SectionCard>

          {/* QA-457 — 주문 상태 변경(주문 단위). 결제상태 변경과 별개로 고도몰 주문 상태(입금대기~구매확정)를 바로 바꾼다. */}
          {ORDER_STATUS_CHANGE_ROLES.some((r) => roles.includes(r)) ? (
            <SectionCard title={t('orders.statusChange.title', '주문상태 변경')} data-testid="order-status-change-card">
              <OrderStatusChangeForm
                orderIds={[order.orderId]}
                allowPurchaseConfirm={canConfirmPurchase(roles)}
                allowCancel={canCancelPendingOrder(roles)}
              />
            </SectionCard>
          ) : null}

          {canPaymentSync || showConfirmDeposit || showRestorePending || showPgRecover ? (
            <OrderPaymentStatusBox targets={paymentStatusTargets} onSelect={onPaymentStatusSelect} />
          ) : null}

          {/* 관리자 메모(be-order feat/admin-order-memo) — 관리자 메모 한 줄 + 고객 요청·상담 이력(memoEntries).
              읽기 전용. 메모가 있는 주문에서만 카드를 노출한다(없으면 null). */}
          <OrderAdminMemoSection order={order} />
          {/* QA-292 — 관리자 메모 여러 건(등록·수정·삭제). 위 카드(한 줄 메모·고객 요청·상담 이력)는 그대로 둔다. */}
          <OrderAdminMemoBoard order={order} currentAdmin={currentAdmin} />

          {/* 현금영수증(order V76) — 신청·발급 이력, 실패 재발급·정보 변경 후 발급·취소. 신청·이력이 없거나 BE 배포 전이면 숨김. */}
          <OrderCashReceiptSection orderId={order.orderId} />

          {/* QA-144 [D-18/ID144] — 이벤트 응모 입력값(label/value) read-only.
              eventInputs 가 있는 주문(이벤트 응모 상품)에서만 카드를 노출한다(없으면 null). */}
          <EventApplicationInputsSection order={order} />

          {/* 항목9(b) — 상품 추가입력(product collection) 값 read-only.
              be-catalog admin endpoint 소비(별도 query). 추가입력이 있는 주문에서만 노출(없으면 null). */}
          <ProductCollectionInputsSection orderId={order.orderId} inlineProductCodes={inlineCollectionCodes} />

          {/* 열 때만 그린다(닫힌 채로는 조회 훅도 없음). */}
          {logOpen ? <OrderLogDialog orderId={order.orderId} open onOpenChange={setLogOpen} /> : null}

          <SectionCard title={t('orders.detail.timeline')}>
            {order.timeline.length === 0 ? (
              <p className="text-sm text-[color:var(--content-muted)]">
                {t('orders.detail.timelineEmpty')}
              </p>
            ) : (
              <ol className="space-y-2">
                {order.timeline.map((entry, idx) => (
                  <li
                    key={`${entry.status}-${entry.occurredAt}-${idx}`}
                    className="flex items-baseline gap-3 text-sm"
                  >
                    <time className="text-[color:var(--content-muted)]">
                      {formatDateTime(entry.occurredAt)}
                    </time>
                    <span className="font-medium text-[color:var(--content-strong)]">
                      {labelOf(ORDER_STATUS_LABELS, entry.status)}
                    </span>
                    {entry.reason ? (
                      <span className="text-[color:var(--content-default)]">
                        — {entry.reason}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            )}
          </SectionCard>

          <SectionCard title={t('orders.detail.claims')}>
            {order.claims.length === 0 ? (
              <p className="text-sm text-[color:var(--content-muted)]">
                {t('orders.detail.claimsEmpty')}
              </p>
            ) : (
              <ul className="space-y-2">
                {order.claims.map((claim) => (
                  <li
                    key={claim.claimId}
                    className="flex items-center justify-between rounded-md border p-3 text-sm"
                    style={{ borderColor: 'var(--surface-1)' }}
                  >
                    <span>
                      <span className="font-medium">
                        {labelOf(CLAIM_TYPE_LABELS, claim.claimType)}
                      </span>
                      {claim.reasonCode ? (
                        <span className="ml-2 text-[color:var(--content-muted)]">
                          {labelOf(CLAIM_REASON_CODE_LABELS, claim.reasonCode)}
                        </span>
                      ) : null}
                    </span>
                    <OrderStatusPill
                      displayStatus={claim.displayStatus}
                      displayLabelCode={
                        (claim as { displayLabelCode?: string | null }).displayLabelCode
                      }
                      orderId={order.orderId}
                    />
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>

      {/* 운영 액션 바 — 이커머스 어드민 패턴(round10): 하단 sticky bar 로 스크롤 중에도
          접근 가능. 좌측에 출력물 메뉴(보조), 우측에 현 단계 적용 가능한 운영 액션만
          의미 그룹(진행 · 클레임)으로 묶어 노출. 적용 불가 액션은 렌더하지 않는다(따닥따닥
          밀집 해소). 각 그룹의 주 액션 1개만 primary, 나머지 outline/danger 로 위계 구분.
          ⚠ 핸들러·모달·RBAC·상태 게이트는 무수정 — 표현/노출조건/레이아웃만 변경. */}
      <footer
        className="sticky bottom-0 z-10 -mx-6 mt-2 flex flex-col gap-3 px-6 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between lg:-mx-8 lg:px-8"
        style={{
          background: 'color-mix(in srgb, var(--surface-0) 92%, transparent)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid var(--divider-strong)',
        }}
        aria-label={t('orders.detail.actionsLabel')}
      >
        {/* 16차 TASK-8 — 출력물(거래명세서/주문내역서/견적서) 진입(좌측, 보조 액션). */}
        <PrintDocumentMenu orderId={orderId} order={order} />

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {showFulfillmentProcessedHint ? (
            <span
              className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium"
              style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
              data-testid="fulfillment-processed-hint"
            >
              {t('orders.detail.fulfillmentProcessed')}
            </span>
          ) : null}

          {/* 진행 그룹 — 결제/이행 진척 액션(입금확인 · 상품준비중 · 픽업확정 · 구매확정). */}
          {hasProgressAction ? (
            <div
              className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center"
              aria-label={t('orders.detail.actionGroup.progress')}
            >
              {/* M-4 ④ [ID52] — 무통장 입금확인. 입금대기+무통장 주문에서만 노출, 클릭 시
                  결제완료 전환. 그 외 상태는 BE 가 409 로 거부(친절 메시지). 한국어 전용. */}
              {showRestorePending ? (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setRestorePendingOpen(true)}
                  data-action="restore-pending"
                >
                  취소 복원
                </Button>
              ) : null}
              {showConfirmDeposit ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setConfirmDepositOpen(true)}
                  data-action="confirm-deposit"
                >
                  입금확인
                </Button>
              ) : null}
              {showFulfillmentComplete ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setActiveAction('fulfillment-complete')}
                  data-action="fulfillment-complete"
                >
                  {t('orders.actions.fulfillment-complete', '배송완료 처리')}
                </Button>
              ) : null}
              {showFulfillmentReady ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setActiveAction('fulfillment-ready')}
                  data-action="fulfillment-ready"
                >
                  {t('orders.actions.fulfillment-ready')}
                </Button>
              ) : null}
              {/* 위험 액션(상품준비 실패)은 위계상 보조 — outline 로 시각 약화하되
                  danger 색 글자/테두리로 명확히 식별(주 액션 primary 대비 부각 방지). */}
              {showFulfillmentFail ? (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  className="w-full sm:w-auto"
                  style={{
                    color: 'var(--signal-danger)',
                    borderColor: 'var(--signal-danger)',
                  }}
                  onClick={() => setActiveAction('fulfillment-fail')}
                  data-action="fulfillment-fail"
                >
                  {t('orders.actions.fulfillment-fail')}
                </Button>
              ) : null}
              {showPickupConfirm ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setPickupModalOpen(true)}
                  data-action="pickup-confirm"
                >
                  {t('orders.actions.pickup-confirm')}
                </Button>
              ) : null}
              {showConfirmPurchase ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setConfirmPurchaseOpen(true)}
                  data-action="confirm-purchase"
                >
                  {t('orders.actions.confirm-purchase')}
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* 진행·클레임 그룹 모두 노출될 때만 구분선. */}
          {hasProgressAction && hasClaimAction ? (
            <Separator
              orientation="vertical"
              className="hidden h-6 sm:block"
              aria-hidden="true"
            />
          ) : null}

          {/* 클레임 그룹 — 오픈 취소요청에 대한 승인/반려(승인=주, 반려=보조). */}
          {hasClaimAction ? (
            <div
              className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center"
              aria-label={t('orders.detail.actionGroup.claim')}
            >
              {showCancelApprove ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setActiveAction('cancel-approve')}
                  data-action="cancel-approve"
                >
                  {t('orders.actions.cancel-approve')}
                </Button>
              ) : null}
              {/* 반려는 승인(primary) 대비 보조 — ghost 로 시각 약화하되 라벨로 식별. */}
              {showCancelReject ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => setActiveAction('cancel-reject')}
                  data-action="cancel-reject"
                >
                  {t('orders.actions.cancel-reject')}
                </Button>
              ) : null}
              {pendingCancelClaim ? (
                <Link
                  to={`/claims/${pendingCancelClaim.claimId}`}
                  className="inline-flex h-9 items-center rounded-md border px-3 text-sm font-medium"
                  style={{ borderColor: 'var(--divider-strong)', color: 'var(--content-strong)' }}
                  data-testid="order-cancel-claim-link"
                >
                  {t('orders.detail.cancelClaimLink', '취소 신청 — 클레임에서 승인·반려')}
                </Link>
              ) : null}
            </div>
          ) : null}

          {/* 빈 액션 graceful — 완료/취소 등 운영 액션이 0개면 어색한 빈 영역 대신
              미세 힌트로 대체(출력물 메뉴는 좌측에 항상 유지). */}
          {!hasAnyOperationAction ? (
            <span
              className="text-xs"
              style={{ color: 'var(--content-muted)' }}
              data-testid="no-operation-actions"
            >
              {t('orders.detail.noActions')}
            </span>
          ) : null}
        </div>
      </footer>

      {/* 열 때만 마운트 — 닫혀 있는 동안 동기화·메모 훅을 만들지 않는다. */}
      {paymentSyncOpen ? (
        <OrderPaymentSyncDialog orderId={orderId ?? order.orderId} open onOpenChange={setPaymentSyncOpen} />
      ) : null}
      {pgRecoverRunning ? (
        <PgRecoverRunner orderId={orderId ?? order.orderId} onDone={() => setPgRecoverRunning(false)} />
      ) : null}

      {activeAction ? (
        <OrderActionModal
          action={activeAction}
          orderNo={order.orderNo}
          isOpen
          isSubmitting={actionMutation.isPending}
          errorMessage={actionErrorMessage}
          expectedPickupCode={order.pickupInfo?.pickupCode ?? null}
          onSubmit={handleSubmit}
          onCancel={() => setActiveAction(null)}
        />
      ) : null}

      {pickupModalOpen && order.pickupInfo?.pickupId ? (
        <OrderPickupConfirmModal
          orderNo={order.orderNo}
          expectedPickupCode={order.pickupInfo.pickupCode ?? null}
          isOpen
          isSubmitting={pickupConfirmMutation.isPending}
          errorMessage={
            pickupConfirmMutation.error
              ? friendlyMessage(pickupConfirmMutation.error, t('orders.actionModal.unknownError'))
              : null
          }
          onSubmit={(body) => {
            if (!order.pickupInfo?.pickupId) return;
            pickupConfirmMutation.mutate(
              {
                pickupId: order.pickupInfo.pickupId,
                body,
              },
              { onSuccess: () => setPickupModalOpen(false) },
            );
          }}
          onCancel={() => setPickupModalOpen(false)}
        />
      ) : null}

      {confirmPurchaseOpen ? (
        <OrderActionModal
          action="confirm-purchase"
          orderNo={order.orderNo}
          isOpen
          isSubmitting={confirmPurchaseMutation.isPending}
          errorMessage={
            confirmPurchaseMutation.error
              ? friendlyMessage(
                  confirmPurchaseMutation.error,
                  t('orders.actionModal.unknownError'),
                )
              : null
          }
          onSubmit={(body) => {
            if (!orderId) return;
            confirmPurchaseMutation.mutate(
              { orderId, body },
              { onSuccess: () => setConfirmPurchaseOpen(false) },
            );
          }}
          onCancel={() => setConfirmPurchaseOpen(false)}
        />
      ) : null}

      {infoEditOpen ? (
        <OrderInfoEditDialog
          orderId={orderId ?? order.orderId}
          initial={orderInfoValuesOf(order)}
          locale={locale}
          onClose={() => setInfoEditOpen(false)}
        />
      ) : null}

      {manualRefundOpen && refundPayment ? (
        <OrderManualRefundDialog
          orderId={orderId ?? order.orderId}
          payment={refundPayment}
          manualRefundOnly
          legacyPg={refundFlags?.legacyPg ?? null}
          onClose={() => setManualRefundOpen(false)}
        />
      ) : null}

      {restorePendingOpen ? (
        <DepositConfirmModal
          orderNo={order.orderNo}
          isOpen
          isSubmitting={restorePendingMutation.isPending}
          errorMessage={
            restorePendingMutation.error
              ? // 409(가상계좌·결제 이력·클레임·쿠폰 사용)는 BE 사유를 그대로.
                restorePendingMutation.error instanceof ApiClientError && restorePendingMutation.error.message
                ? restorePendingMutation.error.message
                : friendlyMessage(restorePendingMutation.error, t('orders.actionModal.unknownError'))
              : null
          }
          copy={{
            title: '취소 복원',
            description: `주문 ${order.orderNo} 을(를) 입금대기로 되돌립니다. 고객 입금을 확인한 뒤 [입금확인]으로 결제완료 처리하세요. 고객에게 알림은 가지 않습니다.`,
            memoLabel: '복원 사유 (선택)',
            placeholder: '예: 고객 입금 요청(10/21 전화)',
            submitLabel: '취소 복원',
          }}
          onSubmit={(reason) => {
            if (!orderId) return;
            restorePendingMutation.mutate(
              { orderId, reason },
              { onSuccess: () => setRestorePendingOpen(false) },
            );
          }}
          onCancel={() => {
            restorePendingMutation.reset();
            setRestorePendingOpen(false);
          }}
        />
      ) : null}

      {confirmDepositOpen ? (
        <DepositConfirmModal
          orderNo={order.orderNo}
          isOpen
          isSubmitting={confirmDepositMutation.isPending}
          errorMessage={
            confirmDepositMutation.error
              ? friendlyMessage(
                  confirmDepositMutation.error,
                  t('orders.actionModal.unknownError'),
                )
              : null
          }
          onSubmit={(reason) => {
            if (!orderId) return;
            confirmDepositMutation.mutate(
              { orderId, reason },
              { onSuccess: () => setConfirmDepositOpen(false) },
            );
          }}
          onCancel={() => setConfirmDepositOpen(false)}
        />
      ) : null}
    </PageShell>
  );
};

interface InfoBlockProps {
  label: string;
  value: ReactNode;
}

const InfoBlock = ({ label, value }: InfoBlockProps) => (
  <div className="mb-2 grid grid-cols-[110px_minmax(0,1fr)] items-baseline gap-2 text-sm">
    <span className="text-[color:var(--content-muted)]">{label}</span>
    {/* QA-370 — 공백 없는 긴 값(이메일·주문번호)도 칸 안에서 줄바꿈(종전엔 칸 밖으로 넘쳐 잘렸다). */}
    <span className="min-w-0 text-[color:var(--content-strong)] [overflow-wrap:anywhere]">{value}</span>
  </div>
);

interface OrderItemsTableProps {
  items: ReadonlyArray<OrderDetail['items'][number]>;
  /**
   * QA-302 — 상품 추가입력(이름·연락처·텍스트 옵션 등)을 품목 행에 함께(고도몰 주문상품 칸처럼). 상품코드로 품목에 붙이고,
   * 같은 상품 라인이 여러 개면 첫 줄에만(입력이 아직 품목 단위로 연결되지 않아 — order_item_id 미연결).
   */
  collectionGroups?: ReadonlyArray<ProductCollectionGroup>;
  /** 원문보기로 원문을 받은 상태(마스킹 배지 대신 원문 표시). */
  collectionRaw?: boolean;
  /** 배송비·송장번호 열과 표 하단 합계(총 상품금액·총 배송비·총 할인·총 주문금액·실 결제금액)·관리자 메모 한 줄. */
  order?: OrderDetail;
}

type Money = { amount: number; currencyCode: string };

/**
 * 관리자 주문 상세 주문상품 확장 필드(order release-qa/v1.0.19, 고도몰 OrderAdmin.php 1431~1454 계산식).
 * generated 타입 반영 전이라 안전 캐스팅으로 읽는다. 없으면 열·행을 생략한다(구버전 응답 호환).
 */
interface OrderItemExtra {
  productCode?: string | null;
  /** 주문 시점 자체상품코드(예 L100006202-R). 없으면 키 없음. */
  selfProductCode?: string | null;
  /** QA-527 주문한 옵션의 자체옵션코드(예 C). 자체상품코드 뒤에 붙여 내부 DB 와 연결한다. 없으면 키 없음. */
  optionCode?: string | null;
  /** 품목 처리상태 표시 문구 — 고도몰 전용 상태(구매발주 등)는 코드 없이 문구만 오므로 이것을 우선 표시. */
  statusLabel?: string | null;
  statusLabelCode?: string | null;
}
interface OrderTotalsExtra {
  totalGoodsAmount?: Money | null;
  /** 선결제 배송비(order #72 — 착불분은 빠진다). */
  shippingFee?: Money | null;
  /** QA-469 착불 배송비 — 결제 금액에 없고 배송 때 고객이 낸다. 착불이 없으면 키 생략. */
  codShippingFee?: Money | null;
  totalDiscountAmount?: Money | null;
  /** 마일리지·예치금 차감 전 주문금액(= 결제 + 마일리지 + 예치금). */
  totalOrderAmount?: Money | null;
  /** 실 결제금액. */
  payableAmount?: Money | null;
  /** 쿠폰 할인 — 총 할인금액에 이미 포함(내역 표시용). */
  couponDiscount?: Money | null;
  /** 마일리지·예치금 사용 — 총 주문금액에서 빠져 실 결제금액이 된다. */
  mileageUsed?: Money | null;
  depositUsed?: Money | null;
  adminMemo?: string | null;
}

const moneyText = (m: Money | null | undefined): string | null =>
  m && typeof m.amount === 'number' ? formatPaymentAmount(m) : null;

/**
 * QA-469 배송비 표기 — 착불만이면 "₩3,000(착불)", 선불·착불이 섞이면 "₩2,500 + 착불 ₩3,000", 착불이 없으면 선결제 배송비.
 * 착불 배송비는 실 결제금액에 들어가지 않는다(BE order #72).
 */
const shippingFeeText = (
  prepaid: Money | null | undefined,
  cod: Money | null | undefined,
  codLabel = '착불',
): string | null => {
  const prepaidText = moneyText(prepaid);
  const codText = cod && cod.amount > 0 ? moneyText(cod) : null;
  if (!codText) return prepaidText;
  if (prepaid && prepaid.amount > 0 && prepaidText) return `${prepaidText} + ${codLabel} ${codText}`;
  return `${codText}(${codLabel})`;
};

/** 차감 금액 표기 — 0 보다 크면 "- ₩5,000"(거래명세서와 같은 표기), 0 이면 그대로. */
const minusText = (m: Money | null | undefined): string | null => {
  const text = moneyText(m);
  return text && m && m.amount > 0 ? `- ${text}` : text;
};

/** 관리자 메모 한 줄 요약(전체는 아래 관리자 메모 영역). */
const firstLine = (memo: string | null | undefined, max = 60): string | null => {
  const line = (memo ?? '').split('\n').map((l) => l.trim()).find(Boolean);
  if (!line) return null;
  return line.length > max ? `${line.slice(0, max)}…` : line;
};

/*
 * 주문상품 표 — 운영 요청: 배송비가 거래명세서 팝업에만 있어 한 번에 볼 수 없었다. 주문 단위 정보까지 표 안에 모은다.
 * - 품목 열: 상품 · 상품코드(자체상품코드) · 수량 · 상품금액(단가) · 총 상품금액(단가×수량) · 처리상태.
 * - 배송비·송장번호는 주문 단위(배송 1건) 값이라 첫 행에 행 병합으로 한 번만 둔다(고도몰 주문상품 표의 배송비 칸과 같음).
 * - 표 하단(tfoot): 총 상품금액 · 총 배송비 · 총 할인금액 · 총 주문금액 · 실 결제금액 · 관리자 메모 한 줄.
 * 값이 없는 열·행은 생략한다(구버전 응답 호환).
 */
const OrderItemsTable = ({ items, order, collectionGroups = [], collectionRaw = false }: OrderItemsTableProps) => {
  const { t } = useTranslation('orders');
  const extras = items.map((i) => i as unknown as OrderItemExtra);
  // 상품코드 → 그 상품의 첫 품목 인덱스(입력값을 붙일 줄).
  // QA-302 — 주문 상세 응답의 품목별 입력(이관 주문의 고도몰 텍스트 옵션 등). catalog 그룹이 없는 품목에 쓴다.
  const orderInputsByItem = placeOrderItemInputs(items, readOrderInputRows(order));
  // 이관 주문 품목은 order-service 값이 기준 — catalog 그룹을 붙이지 않는다(BE 회신 10/16).
  const migratedItemIds = migratedInputItemIds(items, readOrderInputRows(order));
  const collectionByIndex = new Map<number, ProductCollectionGroup>();
  collectionGroups.forEach((g) => {
    if (!g.productCode) return;
    const idx = extras.findIndex((e, i) => e.productCode === g.productCode && !migratedItemIds.has(items[i]!.orderItemId));
    if (idx >= 0 && !collectionByIndex.has(idx)) collectionByIndex.set(idx, g);
  });
  const showCode = extras.some((e) => e.productCode || e.selfProductCode || e.optionCode);
  // QA-625 상품 환불(환불완료·부분환불)도 처리상태 칸에 보인다.
  const showStatus = extras.some((e) => e.statusLabel || e.statusLabelCode) || items.some(hasItemRefund);
  const totals = (order ?? {}) as unknown as OrderTotalsExtra;
  const shippingFee = shippingFeeText(totals.shippingFee, totals.codShippingFee, t('orders.detail.cod', '착불'));
  const tracking = order?.shipmentInfo?.trackingNo
    ? [courierDisplayName(order.shipmentInfo), order.shipmentInfo.trackingNo].filter(Boolean).join(' ')
    : null;
  const showShipping = shippingFee !== null;
  const showTracking = tracking !== null;
  const columnCount = 4 + Number(showCode) + Number(showShipping) + Number(showStatus) + Number(showTracking);
  // QA-292 — 실 결제금액이 총 주문금액과 달라 보이던 문제: 마일리지·예치금 차감이 표에 없었다(거래명세서에만 있음).
  // 계산(고도몰 OrderAdmin.php 1431~1454, BE 같은 식): 총 주문금액 = 총 상품금액 + 총 배송비 − 총 할인금액(쿠폰 포함),
  // 실 결제금액 = 총 주문금액 − 마일리지 − 예치금. 쿠폰은 총 할인금액 안의 내역이라 들여 쓴 하위 행으로만 보인다.
  const couponText = totals.couponDiscount && totals.couponDiscount.amount > 0 ? minusText(totals.couponDiscount) : null;
  const summaryRows = (
    [
      [t('orders.detail.totalGoodsAmount', '총 상품금액'), moneyText(totals.totalGoodsAmount) ?? ''],
      [t('orders.detail.totalShippingFee', '총 배송비'), shippingFee ?? ''],
      [t('orders.detail.totalDiscountAmount', '총 할인금액'), minusText(totals.totalDiscountAmount) ?? ''],
      [t('orders.detail.couponDiscountInTotal', '└ 쿠폰 할인'), couponText ?? '', 'sub'],
      [t('orders.detail.totalOrderAmount', '총 주문금액'), moneyText(totals.totalOrderAmount) ?? '', 'strong'],
      [t('orders.detail.mileageUsedRow', '마일리지 사용'), minusText(totals.mileageUsed) ?? ''],
      [t('orders.detail.depositUsedRow', '예치금 사용'), minusText(totals.depositUsed) ?? ''],
      [t('orders.detail.payableAmount', '실 결제금액'), moneyText(totals.payableAmount) ?? '', 'strong'],
      [t('orders.detail.adminMemoSummary', '관리자 메모'), firstLine(totals.adminMemo) ?? '', 'text'],
    ] as Array<[string, string, string?]>
  ).filter((r) => r[1] !== '');
  if (items.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('orders.detail.itemsEmpty')}
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('orders.detail.itemName')}</TableHead>
          {showCode ? <TableHead>{t('orders.detail.itemCode', '상품코드')}</TableHead> : null}
          <TableHead>{t('orders.detail.itemQty')}</TableHead>
          <TableHead>{t('orders.detail.itemUnitPrice')}</TableHead>
          <TableHead>{t('orders.detail.itemLineAmount')}</TableHead>
          {showShipping ? <TableHead>{t('orders.detail.itemShippingFee', '배송비')}</TableHead> : null}
          {showStatus ? <TableHead>{t('orders.detail.itemStatus', '처리상태')}</TableHead> : null}
          {showTracking ? <TableHead>{t('orders.detail.trackingNo')}</TableHead> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item, idx) => {
          const extra = extras[idx] ?? {};
          // QA-R6 I: 옵션 표기 — variantLabel 단일 폴백이 아니라 구조화 selectedOptions(있으면)
          // 우선으로 "옵션명: 값 / …" 조합. 조합/추가옵션 주문에서 옵션이 '-' 로 비던 결함 대응.
          const option = formatOrderItemOption(item);
          return (
            <TableRow key={item.orderItemId}>
              <TableCell className="text-[color:var(--content-default)]">
                {item.productName}
                {option ? (
                  <span className="ml-1 text-[color:var(--content-muted)]">{option}</span>
                ) : null}
                {collectionByIndex.get(idx) ? (
                  <ul className="mt-1 space-y-0.5 text-[12px]" data-sentry-mask data-testid="order-item-collection">
                    {collectionByIndex.get(idx)!.inputs.map((input, i) => (
                      <li key={`${input.label}-${i}`}>
                        <span className="text-[color:var(--content-muted)]">{input.label} : </span>
                        <span className="text-[color:var(--content-strong)]">{input.value || '-'}</span>
                        {input.masked && !collectionRaw ? (
                          <span className="ml-1 text-[10px] text-[color:var(--content-muted)]">
                            ({t('orders.detail.maskedBadge', '마스킹')})
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : orderInputsByItem.get(item.orderItemId)?.length ? (
                  <ul className="mt-1 space-y-0.5 text-[12px]" data-sentry-mask data-testid="order-item-inputs">
                    {orderInputsByItem.get(item.orderItemId)!.map((input, i) => (
                      <li key={`${input.label}-${i}`}>
                        <span className="text-[color:var(--content-muted)]">{input.label} : </span>
                        <span className="text-[color:var(--content-strong)]">{input.value || '-'}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </TableCell>
              {showCode ? (
                <TableCell className="text-[12px] tabular-nums" data-testid="order-item-codes">
                  <div>{extra.productCode ?? '-'}</div>
                  {extra.selfProductCode || extra.optionCode ? (
                    // QA-527 — 자체상품코드 + 자체옵션코드(예 4HAND26429_M07 + C → 4HAND26429_M07C). 한 코드로 이어 보인다(10/26 굵게 표시 제거).
                    <div className="text-[color:var(--content-muted)]" data-testid="order-item-self-code">
                      {t('orders.detail.itemSelfCode', '자체')} {extra.selfProductCode ?? ''}
                      {extra.optionCode ?? ''}
                    </div>
                  ) : null}
                </TableCell>
              ) : null}
              <TableCell>{item.quantity}</TableCell>
              <TableCell>{formatMoney(item.unitPrice)}</TableCell>
              <TableCell>{formatMoney(item.lineAmount)}</TableCell>
              {showShipping && idx === 0 ? (
                <TableCell rowSpan={items.length} className="tabular-nums" data-testid="order-item-shipping">
                  {shippingFee}
                </TableCell>
              ) : null}
              {showStatus ? (
                <TableCell className="whitespace-nowrap" data-testid="order-item-status">
                  {extra.statusLabel || (extra.statusLabelCode ? labelNameForCode(extra.statusLabelCode) : '-')}
                  <ItemRefundNote item={item} testId={`order-item-refund-${item.orderItemId}`} />
                </TableCell>
              ) : null}
              {showTracking && idx === 0 ? (
                <TableCell
                  rowSpan={items.length}
                  className="whitespace-nowrap tabular-nums"
                  data-testid="order-item-tracking"
                >
                  <div className="flex flex-col items-start gap-1">
                    {tracking}
                    <TrackingButton info={order?.shipmentInfo as TrackableShipment | undefined} />
                  </div>
                </TableCell>
              ) : null}
            </TableRow>
          );
        })}
      </TableBody>
      {summaryRows.length > 0 ? (
        <TableFooter data-testid="order-items-summary">
          {summaryRows.map(([label, value, tone]) => (
            <TableRow key={label}>
              <TableCell
                colSpan={columnCount - 1}
                className={`text-right text-[color:var(--content-muted)] ${tone === 'sub' ? 'text-[12px]' : 'text-[13px]'}`}
              >
                {label}
              </TableCell>
              <TableCell
                className={`${tone === 'sub' ? 'text-[12px] text-[color:var(--content-muted)]' : 'text-[13px]'} ${tone === 'text' ? '' : 'whitespace-nowrap tabular-nums'} ${tone === 'strong' ? 'font-semibold text-[color:var(--content-strong)]' : tone === 'sub' ? '' : 'text-[color:var(--content-default)]'}`}
              >
                {value}
              </TableCell>
            </TableRow>
          ))}
        </TableFooter>
      ) : null}
    </Table>
  );
};
